import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Spinner,
  Table,
  Alert,
  Container,
  Col,
} from 'react-bootstrap';
import { useParseSuppliers } from '../hooks/useParseSuppliers';
import type {
  DropdownSelectOption,
  Supplier,
  UserOrganisation,
} from '../types/types';
import { InlineEditText } from '../components/common/InlineTextEdit';
import { useImportSuppliers } from '../hooks/useImportSuppliers';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import DropdownSelect from '../components/common/DropdownSelect';
import { countryList } from '../const/const';

// Map Country objects to Option type
const countryOptions: DropdownSelectOption[] = countryList.map((c) => ({
  id: c.isoCode,
  label: c.countryName,
}));

const ImportBulkSuppliers: React.FC = () => {
  const [rawInput, setRawInput] = useState('');
  const { data, loading, error, parseSuppliers } = useParseSuppliers();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const {
    importSuppliers,
    loading: importLoading,
    error: importError,
    result: importResult,
  } = useImportSuppliers();
  const selectedOrganisation: UserOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  );

  const handleParse = () => {
    if (!rawInput.trim()) return;
    parseSuppliers(rawInput);
  };

  const handleUpload = async () => {
    if (!selectedOrganisation?.organisation?.id) {
      return;
    }
    await importSuppliers(selectedOrganisation.organisation.id, suppliers);
  };

  // when hook data changes (after parse), sync into local editable state
  useEffect(() => {
    if (data && data.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const flattened = data.map((s: any) => ({
        ...s,
        contact_name: s?.contact?.name || '',
        email: s?.contact?.emails?.[0] || '',
        phone: s?.contact?.phones?.[0] || '',
      }));
      setSuppliers(flattened);
    }
  }, [data]);

  const updateField = (idx: number, field: string, value: string) => {
    const updated = [...suppliers];
    if (!updated) {
      return;
    }

    // @ts-expect-error TS3233
    updated[idx] = { ...updated[idx], [field]: value };

    setSuppliers(updated);
  };

  return (
    <Container className="mt-4">
      <Col className="mb-3">
        <Link to="/suppliers">
          <Button variant="secondary">&larr; Back to Suppliers</Button>
        </Link>
      </Col>

      <h1>Bulk Import Suppliers</h1>

      {importError || importResult ? (
        <div className="mt-4">
          {importError && (
            <Alert variant="danger" className="mb-3">
              {importError}
            </Alert>
          )}

          {importResult?.results && (
            <>
              <h5>Import Results</h5>
              <Alert variant="info">
                Successfully imported{' '}
                {
                  importResult.results.filter(
                    (r: { success: boolean }) => r.success
                  ).length
                }{' '}
                out of {importResult.results.length} suppliers.
              </Alert>
              <p>
                Imported suppliers are{' '}
                <mark>marked as imported in the list of suppliers.</mark> Please
                review and make sure that the information is correct.
              </p>
              <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th>Status</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.results.map(
                      (
                        r: {
                          supplier: string;
                          success: boolean;
                          data?: string;
                          error?: {
                            code: string;
                            message: string;
                            details: string;
                          };
                        },
                        idx: number
                      ) => (
                        <tr key={idx}>
                          <td>{r.supplier}</td>
                          <td>
                            {r.success ? (
                              <span className="text-success">✔ Imported</span>
                            ) : (
                              <span className="text-danger">✖ Failed</span>
                            )}
                          </td>
                          <td>
                            {r.success ? (
                              <code>
                                <Link
                                  to={`/suppliers/${r.data}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  details
                                </Link>
                              </code>
                            ) : (
                              <>
                                {/* <div>
                                  <strong>{r.error?.message}</strong>
                                </div> */}
                                {/* TODO: look at the details, if it contains "already exists" it's a duplicate reg nr issue
                                for now just show already exists */}
                                <div className="text-muted small">
                                  possible duplicate supplier information, check
                                  registration number
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <p>
            Paste a list of suppliers (one per line) to extract their details.
          </p>

          <p>
            Best formatting is:
            <mark>
              Company Name, Registration Number, Country, Address, Contact name,
              Email, Phone
            </mark>
          </p>
          <p>
            Separate fields with commas and each supplier on a new line for best
            results
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Insert supplier data</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              maxLength={10000}
              placeholder="Paste supplier list here..."
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              disabled={loading || importLoading}
            />
            <div
              className={`text-end ${
                rawInput.length > 9900 ? 'text-danger' : 'text-muted'
              } small mt-1`}
            >
              {rawInput.length}/{10000}
            </div>
          </Form.Group>

          <Button
            variant="primary"
            onClick={handleParse}
            disabled={loading || importLoading || !rawInput.trim()}
          >
            {loading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Extracting...
              </>
            ) : (
              'Submit'
            )}
          </Button>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {suppliers.length > 0 && !loading && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5>Extracted Suppliers (Click to edit)</h5>
              </div>
              <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Registration #</th>
                      <th>Country</th>
                      <th>Address</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Full Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier, idx) => (
                      <tr key={idx}>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.company_name || ''}
                            onSave={(val) =>
                              updateField(idx, 'company_name', val)
                            }
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.registration_number || ''}
                            onSave={(val) =>
                              updateField(idx, 'registration_number', val)
                            }
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <DropdownSelect
                            options={countryOptions}
                            selectedIds={supplier?.country}
                            placeholder="Select country"
                            onValueChanged={(val) =>
                              // @ts-expect-error: ts(2339)
                              updateField(idx, 'country', val.id)
                            }
                          />
                          {/* <InlineEditText
                            asBlock
                            value={supplier?.country || ''}
                            onSave={(val) => updateField(idx, 'country', val)}
                          /> */}
                        </td>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.address || ''}
                            onSave={(val) => updateField(idx, 'address', val)}
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.contact_name || ''}
                            onSave={(val) =>
                              updateField(idx, 'contact_name', val)
                            }
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.email || ''}
                            onSave={(val) => updateField(idx, 'email', val)}
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <InlineEditText
                            asBlock
                            value={supplier?.phone || ''}
                            onSave={(val) => updateField(idx, 'phone', val)}
                          />
                        </td>
                        <td
                          style={{
                            position: 'relative',
                            maxWidth: '200px',
                            wordBreak: 'break-all',
                          }}
                        >
                          {supplier?.original || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="my-3">
                <Button
                  className="w-100"
                  onClick={() => handleUpload()}
                  disabled={importLoading}
                >
                  Import {suppliers.length} suppliers
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default ImportBulkSuppliers;
